import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/auth-context';
import { useResume } from '@/contexts/resume-context';
import { useToast } from '@/hooks/use-toast';
import { parseResume } from '@/lib/resume-parser';
import { isGeminiConfigured } from '@/lib/resume-parser';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Loader2 } from 'lucide-react';

/**
 * This component handles resume processing after user registration
 * It should be rendered at the app root level to catch post-registration resume processing
 */
export default function PostRegistrationHandler() {
  const { user } = useAuth();
  const { pendingFile, setPendingFile, setParsedResume, clearResumeData } = useResume();
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const [isProcessing, setIsProcessing] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const geminiConfigured = isGeminiConfigured();
  
  // Extract query parameters
  const queryParams = new URLSearchParams(location.search);
  const hasPendingResumeParam = queryParams.get('resume') === 'pending';

  useEffect(() => {
    // Check if we should process a resume
    const shouldProcessResume = user && pendingFile && hasPendingResumeParam && geminiConfigured;
    
    if (shouldProcessResume && !isProcessing) {
      processResume();
    }
  }, [user, pendingFile, hasPendingResumeParam, geminiConfigured]);

  // Process the pending resume file
  const processResume = async () => {
    if (!pendingFile || !user) return;
    
    setIsProcessing(true);
    setShowDialog(true);
    
    try {
      // Parse the resume
      const parsedData = await parseResume(pendingFile);
      
      // Store the parsed resume data
      setParsedResume(parsedData);
      
      // Clear the pending file
      setPendingFile(null);
      
      // Show success message
      toast({
        title: 'Resume Processed',
        description: 'Your resume has been successfully processed. Your profile will be prefilled with the extracted information.',
      });
      
      // Navigate to onboarding
      navigate('/onboarding?resume=pending');
    } catch (error: any) {
      console.error('Error processing resume:', error);
      
      setError(error.message || 'Failed to process your resume. Please try again.');
      
      // Show error toast
      toast({
        variant: 'destructive',
        title: 'Resume Processing Error',
        description: error.message || 'Failed to process your resume. Please try again.',
      });
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle dialog cancellation
  const handleCancel = () => {
    setShowDialog(false);
    clearResumeData();
    
    // Remove the query parameter but keep the rest of the URL path
    const newUrl = location.pathname;
    navigate(newUrl, { replace: true });
  };

  // Handle confirmation - navigate to onboarding
  const handleConfirm = () => {
    setShowDialog(false);
    navigate('/onboarding');
  };

  // Don't render anything if no processing is needed
  if (!hasPendingResumeParam) {
    return null;
  }

  return (
    <AlertDialog open={showDialog} onOpenChange={setShowDialog}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {isProcessing ? 'Processing Your Resume' : (error ? 'Resume Processing Error' : 'Resume Processed')}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {isProcessing ? (
              <div className="flex flex-col items-center py-4">
                <Loader2 className="h-8 w-8 animate-spin mb-4" />
                <p>Please wait while we process your resume to prefill your profile...</p>
              </div>
            ) : error ? (
              <div>
                <p>There was a problem processing your resume:</p>
                <p className="text-destructive mt-2">{error}</p>
                <p className="mt-2">You can still complete your profile manually.</p>
              </div>
            ) : (
              <p>Your resume has been successfully processed. Your profile will be prefilled with the extracted information.</p>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          {!isProcessing && (
            <>
              <AlertDialogCancel onClick={handleCancel}>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleConfirm}>
                {error ? 'Complete Profile Manually' : 'Continue to Profile'}
              </AlertDialogAction>
            </>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}